import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

/**
 * Expone a todo el árbol el payload `connected` del WebSocket (ver
 * apps/core/src/modules/websocket/routes.ts). Existe porque `useSensors` y
 * `useWeatherStations` se llaman desde componentes que no reciben `user` por
 * props (HomePage, SoilOverview, BatteryPage, LocationPage, WeatherPage) y
 * necesitan saber si este tenant tiene permitidos los datos demo.
 *
 * `isDemoTenant` llega del servidor, no se infiere acá: la regla es "los
 * demos solo para el cliente Demo farm" y el frontend no tiene forma
 * confiable de saber a qué tenant pertenece la sesión.
 *
 * `ready` distingue "todavía no llegó el payload del WS" de "llegó y dice
 * que no es demo". Sin eso, los hooks decidirían con `isDemoTenant === false`
 * durante el primer render y ni siquiera el tenant Demo vería sus datos.
 *
 * — DIZ
 */

export interface Session {
  userId?: string;
  email?: string;
  name?: string | null;
  role?: string;
  tenantId?: string | null;
  enabledModules?: string[];
  isDemoTenant?: boolean;
}

interface SessionContextValue {
  session: Session | null;
  /** true en cuanto llegó el payload `connected` del WS. */
  ready: boolean;
  isDemoTenant: boolean;
}

const SessionContext = createContext<SessionContextValue>({
  session: null,
  ready: false,
  isDemoTenant: false,
});

/**
 * Si el WS no responde en este plazo, se sigue adelante igual con
 * `isDemoTenant: false`. Sin esta red de seguridad, un WS caído dejaría las
 * páginas de datos cargando para siempre — peor que el problema original.
 * El default es el seguro: ante la duda, NO enseñar datos demo.
 */
const SESSION_TIMEOUT_MS = 4000;

export function SessionProvider({ session, children }: { session: Session | null; children: ReactNode }) {
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (session != null) return;
    const id = setTimeout(() => setTimedOut(true), SESSION_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, [session]);

  const value: SessionContextValue = {
    session,
    ready: session != null || timedOut,
    isDemoTenant: session?.isDemoTenant === true,
  };
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  return useContext(SessionContext);
}
